// src/router/routes.ts
import type { RouteRecordRaw } from "vue-router";
import { useAuthStore, Perm, has } from "@/admin/stores/Auth/auth";

// ---------- Auth & System ----------
const LoginView = () => import("@/admin/views/Auth/LoginView.vue");
const ForbiddenView = () => import("@/admin/views/Forbidden.vue");

// ---------- Rewards / Affiliate ----------
const ReferFriendsEarnRewards = () =>
  import("@/affiliate/layout/ReferFriendsEarnRewards.vue");
const yiksiReferral = () => import("@/affiliate/layout/yiksi_referral.vue");
const inviteFriend = () => import("@/affiliate/layout/InviteFriend.vue");

// ---------- Admin (Protected Shell at "/admin") ----------
const AdminLayout = () => import("@/admin/layouts/index.vue");
const AllPostView = () => import("@/admin/views/Blog/BlogList.vue");
const BlogEditor = () => import("@/admin/views/Blog/BlogPost.vue");
const TestimonialView = () =>
  import("@/admin/views/Testimonials/TestimonialList.vue");
const PrivacyTitleView = () =>
  import("@/admin/views/Privacy/PrivacyTitleView.vue");
const TermsView = () => import("@/admin/views/Terms/terms.vue");
const KycView = () => import("@/admin/views/Kyc/kyc.vue");
const SettingsLayout = () => import("@/admin/layouts/SettingsLayout.vue");
const SettingsUsers = () => import("@/admin/views/Users/SettingsUsers.vue");
const SettingsRoles = () => import("@/admin/views/Roles/SettingsRoles.vue");
const SystemSettings = () =>
  import("@/admin/views/Settings/SystemSettings.vue");
const RoleFormView = () => import("@/admin/views/Roles/RoleFormView.vue");

// ---------- Front (Public at "/") ----------
const FrontNotFound = () => import("@/front/views/NotFound/FrontNotFound.vue");
const FrontLayout = () => import("@/front/layout/index.vue");
const HomeSection = () => import("@/front/views/Home/HomeSection.vue");
const AboutView = () => import("@/front/views/About/AboutView.vue");
const BlogList = () => import("@/front/views/Blog/BLogList.vue");
const SinglePost = () => import("@/front/views/Blog/SinglePost.vue");
const Contact = () => import("@/front/views/Contact/contact.vue");
const DownloadPage = () => import("@/front/views/DownloadPage.vue");
const FrontPrivacy = () => import("@/front/views/Privacy/Privacy.vue");
const FrontTerms = () => import("@/front/views/Terms/Terms.vue");
const RiskWarning = () => import("@/front/views/riskwarning/riskwarning.vue");
const KycAmlPage = () => import("@/front/views/Kyc/KycAmlPage.vue");
const DeleteAgreement = () => import("@/front/views/DeleteAgreement/Index.vue");

// ---------- Product Pages ----------
const ProductsView = () => import("@/front/views/Products/ProductsView.vue");
const Products = () => import("@/front/views/Products/Products.vue");

const WalletView = () => import("@/front/views/Products/WalletView.vue");
const otcDesk = () => import("@/front/views/Products/otcDesk.vue"); 
const MerchantsView = () => import("@/front/views/Merchants/MerchantsView.vue"); 
const VirtualCardView = () =>
  import("@/front/views/Products/VirtualCardView.vue");
const StablecoinAPIView = () =>
  import("@/front/views/Products/StablecoinAPIView.vue");

export const routes: RouteRecordRaw[] = [
  // ---------- Front (public) ----------
  {
    path: "/",
    component: FrontLayout,
    children: [
      { path: "", name: "FrontHome", component: HomeSection },
      { path: "about", name: "FrontAbout", component: AboutView },
      { path: "contact", name: "FrontContact", component: Contact },
      { path: "privacy", name: "FrontPrivacy", component: FrontPrivacy },
      { path: "terms", name: "FrontTerms", component: FrontTerms },
      { path: "riskwarning", name: "RiskWarning", component: RiskWarning },
      { path: "blog", name: "FrontBlog", component: BlogList },
      { path: "Downloads", name: "DownloadPage", component: DownloadPage },
      {
        path: "/post/:slug",
        name: "singlepost",
        component: SinglePost,
        props: true,
      },
      // Add flat routes outside
      { path: "products/wallet", name: "WalletPage", component: WalletView },
      { path: "products/otcDesk", name: "otcDesk", component: otcDesk },
      {
        path: "products/virtual-card",
        name: "VirtualCardPage",
        component: VirtualCardView,
      },
      {
        path: "products/stablecoin-api",
        name: "StablecoinAPIPage",
        component: StablecoinAPIView,
      },
      {
        path: "/products/Yiksi-merchants",
        name: "Yiksi-merchants",
        component: MerchantsView,
      },
     
      // Legacy ID route kept for back-compat (same component)
      // routes.ts
      {
        path: "/:slug",
        name: "singlepost",
        component: SinglePost,
        props: true, // passes { slug } as a prop to SinglePost
      },
      {
        path: "/kyc-requirements",
        name: "kyc-requirements",
        component: KycAmlPage,
        alias: ["/kyc"], // optional: /kyc also works
      },

      // ------- Back-compat: redirect any old deep links to the single page -------
      // Old dynamic & tier routes:
      {
        path: "/kyc-aml/kyc-:id(\\d+)",
        name: "kyc-item",
        redirect: { name: "kyc&aml" },
      },
      {
        path: "/kyc&aml/tier&:id(\\d+)",
        name: "kyc&tier&id",
        redirect: { name: "kyc&aml" },
      },
      {
        path: "/kyc&aml/tier&1",
        name: "kyc&tier&1",
        redirect: { name: "kyc&aml" },
      },
      {
        path: "/kyc&aml/tier&2",
        name: "kyc&tier&2",
        redirect: { name: "kyc&aml" },
      },
      {
        path: "/kyc&aml/tier&3",
        name: "kyc&tier&3",
        redirect: { name: "kyc&aml" },
      },
      {
        path: "/account-deletion-agreement",
        name: "DeleteAgreement",
        component: DeleteAgreement,
      },

      // 🔥 Affiliate / Rewards Center
      // -----------------------------------------
      //
      // 🔥 Affiliate / Rewards

      {
        path: ":pathMatch(.*)*",
        name: "FrontNotFound",
        component: FrontNotFound,
      },
    ],
  },

  {
    path: "/affiliate",
    name: "ReferFriendsEarnRewards",
    component: ReferFriendsEarnRewards,
  },
  {
    path: "/yiksiReferral/:id",
    name: "yiksiReferral",
    component: yiksiReferral,
    props: true,
  },
  {
    path: "/inviteFriend/:id",
    name: "inviteFriend",
    component: inviteFriend,
    props: true,
  },
  // ---------- Auth ----------
  {
    path: "/admin-login",
    name: "login",
    component: LoginView,
    meta: { guestOnly: true, hideSidebar: true },
  },

  // ---------- Admin (protected) ----------
  {
    path: "/admin",
    component: AdminLayout,
    meta: { requiresAuth: false },
    children: [
      { path: "", redirect: { name: "All-Post" } },

      {
        path: "All-Post",
        name: "All-Post",
        component: AllPostView,
        meta: { page: "blog", perm: Perm.VIEW },
      },
      {
        path: "blog",
        name: "blog-new",
        component: BlogEditor,
        meta: { page: "blog", perm: Perm.CREATE },
      },
      {
        path: "blog/:id(\\d+)",
        name: "blog-edit",
        component: BlogEditor,
        props: true,
        meta: { page: "blog", perm: Perm.EDIT },
      },

      {
        path: "Testimonial",
        name: "Testimonial",
        component: TestimonialView,
        meta: { page: "testimonials", perm: Perm.VIEW },
      },
      {
        path: "Privacy",
        name: "Privacy",
        component: PrivacyTitleView,
        meta: { page: "privacy", perm: Perm.VIEW },
      },
      {
        path: "Terms",
        name: "Terms",
        component: TermsView,
        meta: { page: "terms", perm: Perm.VIEW },
      },
      {
        path: "kyc",
        name: "kyc",
        component: KycView,
        meta: { page: "kyc", perm: Perm.VIEW },
      },

      {
        path: "settings",
        component: SettingsLayout,
        meta: { page: "settings", perm: Perm.VIEW },
        beforeEnter: (to, _from, next) => {
          const isIndex =
            to.matched.length > 0 &&
            to.matched[to.matched.length - 1].path === "";
          if (!isIndex) return next();
          const auth = useAuthStore();
          const can = (page: string, perm: number) =>
            has(auth.perms?.[page] ?? 0, perm);
          if (can("users", Perm.VIEW)) return next({ name: "SettingsUsers" });
          if (can("settings", Perm.EDIT))
            return next({ name: "SystemSettings" });
          return next();
        },
        children: [
          {
            path: "",
            name: "Settings",
            component: SystemSettings,
            meta: { page: "settings", perm: Perm.VIEW },
          },
          {
            path: "users",
            name: "SettingsUsers",
            component: SettingsUsers,
            meta: { page: "users", perm: Perm.VIEW },
          },
          {
            path: "roles",
            name: "SettingsRoles",
            component: SettingsRoles,
            meta: { page: "settings", perm: Perm.EDIT },
          },
          {
            path: "system",
            name: "SystemSettings",
            component: SystemSettings,
            meta: { page: "settings", perm: Perm.EDIT },
          },
          {
            path: "roles/new",
            name: "roles-create",
            component: RoleFormView,
            meta: { page: "settings", perm: Perm.EDIT },
          },
          {
            path: "roles/:code",
            name: "roles-edit",
            component: RoleFormView,
            props: true,
            meta: { page: "settings", perm: Perm.EDIT },
          },
        ],
      },
    ],
  },

  // -----------------------------------------

  // ---------- 403 + 404 ----------
  {
    path: "/notFound",
    name: "forbidden",
    component: ForbiddenView,
    alias: ["/notfound"],
    meta: { hideSidebar: true },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: ForbiddenView,
    meta: { hideSidebar: true },
  },
];

export default routes;
